import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function EngineeringAuditToilPage() {
  const faqs = [
    {
      question: 'What creates audit toil for engineering teams?',
      answer: 'Manual evidence collection requires pulling PR approvals, branch protection settings, and exception details, often via screenshots and ad-hoc exports that are hard to reproduce.'
    },
    {
      question: 'How does EvidentTrail reduce manual work?',
      answer: 'EvidentTrail connects with a read-only GitHub App and captures approvals, branch protection snapshots, drift changes, and exceptions automatically, then exports evidence packs on demand.'
    },
    {
      question: 'What does EvidentTrail not collect?',
      answer: 'It is metadata-only. EvidentTrail does not store source code, prompts, or free-text review comments.'
    },
    {
      question: 'How long are evidence packs retained?',
      answer: 'Retention is plan-based (7, 30, or 90 days). Guest mode evidence packs are available for 24 hours.'
    }
  ];

  return (
    <MarketingLayout
      title="Reduce Engineering Audit Toil | EvidentTrail"
      description="Replace manual evidence collection with read-only GitHub metadata exports. Generate evidence packs with checksums to support audit evidence collection."
      h1="Reduce Engineering Audit Toil"
      heroText="Generate evidence packs from GitHub metadata without manual screenshots or ad-hoc exports."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Engineering Audit Toil', url: 'https://evidenttrail.com/engineering-audit-toil' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Where Engineering Time Goes</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Audit requests often require engineering teams to assemble evidence from pull request
              approvals, branch protection settings, and emergency exception records. Without a
              centralized audit trail, teams rely on manual screenshots, exported CSVs, and
              repeated explanation emails that are hard to reproduce later.
            </p>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>• Pulling approval history from GitHub for each audit sample</li>
              <li>• Capturing branch protection settings at a specific point in time</li>
              <li>• Reconstructing exceptions after emergency merges</li>
              <li>• Answering follow-up questions without a consistent evidence pack</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail Removes Manual Steps</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail connects to GitHub with a read-only app installation and captures
              approvals, reviews, branch protection snapshots, drift changes, and exception
              records. Evidence packs are generated from this timeline and exported as PDF, CSV,
              JSON, and manifest files with SHA-256 checksums.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Evidence Packs</h3>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li>• Approval and reviewer metadata</li>
                  <li>• Branch protection snapshot details</li>
                  <li>• Manifest checksums for verification</li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Audit Timeline</h3>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li>• Chronological change record</li>
                  <li>• Drift detection events with severity</li>
                  <li>• Exception tracking for bypasses</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What We Do Not Collect</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail is metadata-only. It does not store source code, prompts, review comments,
              or any free-text fields. AI evidence capture relies on explicit GitHub metadata signals
              such as co-author trailers, labels, and Copilot flags.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Retention and Usage Boundaries</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Evidence pack retention is plan-based (7, 30, or 90 days), and monthly pack generation
              quotas are enforced by plan tier. Guest mode provides a 24-hour session window for
              exports without an account.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Outcomes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/continuous-audit-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Continuous Audit Evidence</h3>
              <p className="text-sm text-slate-400">Keep approvals, drift, and exceptions in one timeline.</p>
            </Link>
            <Link to="/auditor-self-serve" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Auditor Self-Serve</h3>
              <p className="text-sm text-slate-400">Offer guest evidence exports without permanent access.</p>
            </Link>
            <Link to="/iso-27001-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">ISO 27001 GitHub Guide</h3>
              <p className="text-sm text-slate-400">Map GitHub evidence exports to ISO 27001 A.8.32 requirements.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Evidence Tools</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">PR Approvals Report</h3>
              <p className="text-sm text-slate-400">Export approvals and reviewer metadata as an evidence pack.</p>
            </Link>
            <Link to="/tools/branch-protection-snapshot" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Branch Protection Snapshot</h3>
              <p className="text-sm text-slate-400">Capture branch protection settings with checksums.</p>
            </Link>
            <Link to="/features/exception-tracking" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Exception Tracking</h3>
              <p className="text-sm text-slate-400">Document emergency bypasses with a full lifecycle record.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
